import { useAuth0 } from "@auth0/auth0-react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import axios, { AxiosRequestConfig } from "axios";
import { endOfDay, format, parseISO, startOfDay } from "date-fns";
import { isUndefined, omitBy, toString } from "lodash";
import React, { useContext } from "react";
import { useTranslation } from "react-i18next";
import { config } from "..";
import { Filters } from "../types/filters";

interface CacheUpdate {
  type: updateEntityTypes;
  // id: number;
  items: { id: number; status: number }[]; //Record<string, any>[];
  // action: "add" | "update" | "delete";
}

export interface QueryRecord {
  entityName: entityTypes;
  query?: QueryData;
}

export interface UpdateResponse {
  errorResponse?: string;
  success: boolean;
}

export interface CircylCrmContextConfig {
  isApiError: boolean;
  errorMessage?: string;
  buttonsLocked: boolean;
  setIsApiError: React.Dispatch<boolean>;
  setErrorMessage: React.Dispatch<string | undefined>;
  setButtonsLocked: React.Dispatch<boolean>;
}

export const CircylCrmContext = React.createContext<CircylCrmContextConfig>({
  isApiError: false,
  errorMessage: undefined,
  setIsApiError: () => {},
  setErrorMessage: () => {},
  buttonsLocked: false,
  setButtonsLocked: () => {},
});
export const CircylCrmContextProvider = CircylCrmContext.Provider;

export type entityTypes =
  | "timesheets"
  | "contacts"
  | "systemusers"
  | "client"
  | "profile"
  | "faq"
  | "notifications"
  | "register"
  | "filters";

export type updateEntityTypes =
  | "timesheets"
  | "systemusers"
  | "profile"
  | "update"
  | "register";

interface QueryData {
  startDate?: Date;
  endDate?: Date;
  id?: string;
  search?: string;
  clientId?: string;
  status?: number[];
}

interface QueryStringData {
  start?: string;
  end?: string;
  page?: string;
  count?: string;
  search?: string;
  clientId?: string;
  status?: string;
}

interface PagingData {
  page: number;
  count: number;
}

interface PagingResponse {
  totalCount: number;
  nextPage: boolean;
  totalPages: number;
}

const extractQueryString = (data: Record<string, string>): string =>
  new URLSearchParams(data).toString();

const buildQueryKey = (
  entityName: entityTypes,
  query?: QueryData,
  paging?: PagingData
) => {
  if (query && paging) {
    return [entityName, query, paging];
  } else if (query) {
    return [entityName, query];
  } else if (paging) {
    return [entityName, paging];
  } else {
    return [entityName];
  }
};

export const useApi = <T>(
  entityName: entityTypes,
  query?: QueryData,
  paging?: PagingData,
  enabled = true
) => {
  const { i18n } = useTranslation();
  const { getAccessTokenSilently, isAuthenticated } = useAuth0();
  const queryKey = buildQueryKey(entityName, query, paging);
  return useQuery<
    | {
        results?: T;
        paging?: PagingResponse;
        filters?: Filters;
      }
    | undefined
  >({
    queryKey,
    queryFn: async () => {
      try {
        const token =
          entityName !== "register"
            ? await getAccessTokenSilently()
            : undefined;

        let uri: string;
        if (query?.id) {
          uri = `${config.apiBaseUrl}/api/${entityName}/${query.id}`;
        } else {
          const queryString: QueryStringData = {
            start: query?.startDate
              ? startOfDay(query.startDate).toISOString()
              : undefined,
            end: query?.endDate
              ? endOfDay(query.endDate).toISOString()
              : undefined,
            page: paging?.page ? toString(paging.page) : undefined,
            count: paging?.count ? toString(paging.count) : undefined,
            search: query?.search,
            clientId: query?.clientId,
            status: query?.status?.join(","),
          };
          const queryStr = extractQueryString({
            ...omitBy(queryString, isUndefined),
            ...omitBy(paging, isUndefined),
          });
          uri = `${config.apiBaseUrl}/api/${entityName}${
            queryStr ? `?${queryStr}` : ""
          }`;
        }
        if (uri.includes("?")) {
          uri = `${uri}&lang=${i18n.language}`;
        } else {
          uri = `${uri}?lang=${i18n.language}`;
        }
        const headers = token
          ? {
              Authorization: `Bearer ${token}`,
            }
          : {};
        const result = await axios.get(uri, {
          headers,
        });
        if (result.data.data) {
          return {
            results: result.data.data as T,
            paging: result.data.paging as PagingResponse,
            filters: result.data.filters,
          };
        } else {
          return { results: result.data as T };
        }
      } catch (ex) {
        console.error("error", ex);
        return { results: [] as T };
      }
    },
    enabled: (isAuthenticated || entityName === "register") && enabled,
  });
};

export const useApiMutation = () => {
  const { errorMessage, isApiError, setErrorMessage, setIsApiError } =
    useContext(CircylCrmContext);
  const { getAccessTokenSilently } = useAuth0();
  const queryClient = useQueryClient();
  const { i18n } = useTranslation();

  const hitApi = async (
    entityName: updateEntityTypes,
    update: Record<string, any>
  ) => {
    const postTypes: updateEntityTypes[] = ["update", "register"];
    const method = postTypes.some((p) => p === entityName) ? "post" : "patch";
    const token =
      entityName !== "register" ? await getAccessTokenSilently() : undefined;
    const url = `${config.apiBaseUrl}/api/${entityName}?lang=${i18n.language}`;

    const headers = token
      ? {
          Authorization: `Bearer ${token}`,
        }
      : {};

    const response = await axios({
      method,
      url,
      data: update,
      headers,
    });

    const data = response.data;
    setIsApiError(!data.success);
    setErrorMessage(data.errorResponse);
    return data;
  };

  const reset = () => {
    setErrorMessage(undefined);
    setIsApiError(false);
  };

  const updateCacheItems = async (cacheUpdate: CacheUpdate) => {
    const { type, items } = cacheUpdate;

    // find all queries of this type.
    const toUpdate = queryClient
      .getQueryCache()
      .getAll()
      .filter((cache) => cache.queryKey[0] === type);

    toUpdate.forEach((update) => {
      const cachedItems: { results: Record<string, any>[] } | undefined =
        queryClient.getQueryData(update.queryKey);
      if (
        !isUndefined(cachedItems?.results) &&
        cachedItems.results.some((ci) => items.some((i) => i.id === ci.id))
      ) {
        // some ids match and need to be updated in the local cache.
        cachedItems.results = cachedItems.results.map((t) => {
          const match = items.find((i) => i.id === t.id);
          if (!isUndefined(match)) {
            // item to update in the cache found.
            t.status = match.status; // set the status.
            t.dirty = true; // mark as dirty to keep timesheet row button disabled.
          }
          return t;
        });
        // update cache.
        queryClient.setQueryData(update.queryKey, cachedItems);
      }
    });
  };

  return {
    apiUpdate: useMutation({
      mutationFn: (variables: {
        entity: updateEntityTypes;
        post: Record<string, any>;
        cacheUpdate?: {
          type: "timesheets";
          // id: number;
          items: { id: number; status: number }[]; //Record<string, any>[];
          action: "update";
        };
        refetchType?: "all" | "none";
      }) => hitApi(variables.entity, variables.post),
      onMutate: async ({ cacheUpdate }) => {
        // update the cache.
        if (!cacheUpdate || cacheUpdate.items.length === 0) {
          return;
        }

        // Return a context with the previous and new todo
        return { queryKey: await updateCacheItems(cacheUpdate) };
      },
      onError(error, { cacheUpdate }, context) {
        console.error(error);
      },
      onSuccess(data, { post, cacheUpdate, refetchType }, context) {
        if (!refetchType || refetchType === "all") {
          queryClient.invalidateQueries({ refetchType });
        }
      },
    }),
    isApiError,
    errorMessage,
    reset,
    updateCacheItems,
  };
};

export const useDownload = () => {
  const { getAccessTokenSilently } = useAuth0();

  const { i18n } = useTranslation();

  const download = async (id: number, weekEnding: string) => {
    const token = await getAccessTokenSilently();
    const url = `${config.apiBaseUrl}/api/download/${id}?lang=${i18n.language}`; // adding language in the request url (rk)
    const headers: AxiosRequestConfig = {
      headers: {
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/pdf",
      },
      responseType: "blob",
    };
    axios
      .get(url, headers)
      .then((response) => response.data)
      .then((blob) => {
        const url = window.URL.createObjectURL(new Blob([blob]));
        const link = document.createElement("a");
        link.href = url;
        link.setAttribute(
          "download",
          `${id}_${format(parseISO(weekEnding), "ddMMyyyy")}.pdf`
        );
        // Append to html link element page
        document.body.appendChild(link);
        // Start download
        link.click();
        // Clean up and remove the link
        link.parentElement?.removeChild(link);
      });
  };

  return { download };
};
